import {
  normalizeImportedAssetFileId,
} from '../sidecar'
import type { ImportedTopologyRef } from '../loaders/types'
import type { ImportedAssetFileDescriptor } from './hydrateImportedTopologySourceBrepRuntime'

/**
 * 在项目文件列表中查找 sidecar 文件。
 *
 * @param projectFiles 项目文件列表。
 * @param sidecarFileId sidecar 文件 ID。
 * @returns 命中的文件描述；未命中时返回 `null`。
 */
export function findImportedSidecarFile<TFile extends ImportedAssetFileDescriptor>(
  projectFiles: TFile[],
  sidecarFileId: string,
): TFile | null {
  const normalizedTargetFileId = normalizeImportedAssetFileId(sidecarFileId)
  return (
    projectFiles.find(
      (projectFile) => normalizeImportedAssetFileId(projectFile.fileId) === normalizedTargetFileId,
    ) ?? null
  )
}

/**
 * 按显式 topo sidecar 引用解析文件 ID。
 *
 * 设计说明：
 * 1. topo sidecar 必须由 GLB 中的 `topologyRef.uri` 显式声明，前端不再按文件名推导；
 * 2. `uri` 始终按“相对当前 GLB 文件”解析，与 BRep sidecar 的路径语义保持一致；
 * 3. 该函数只处理纯路径归一化，不检查文件是否存在；显式 topoRef 缺失文件会在 hydration 层暴露为数据问题。
 *
 * @param glbFileId 当前 GLB 文件 ID。
 * @param topologyRef 当前 body 的 topo sidecar 引用。
 * @returns topo sidecar 文件 ID。
 */
export function resolveImportedTopologySidecarFileId(
  glbFileId: string,
  topologyRef: ImportedTopologyRef,
): string {
  const explicitUri = topologyRef.uri.trim()
  if (explicitUri.startsWith('/')) {
    return normalizeImportedAssetFileId(explicitUri)
  }

  const segments = normalizeImportedAssetFileId(glbFileId).split('/').filter(Boolean)
  segments.pop()
  const baseDirectory = segments.join('/')
  return normalizeImportedAssetFileId(
    baseDirectory.length > 0 ? `${baseDirectory}/${explicitUri}` : explicitUri,
  )
}

